import { useState, useEffect, useRef } from 'react';
import { PieChart, Pie, Cell } from 'recharts';
import SectionEmpty from '../../../SectionEmpty';
import SectionError from '../../../SectionError';
import { T } from '../../../reportTokens';
import { getCurrencySymbol } from '../../../../../utils/currency';

export interface DoughnutSlice {
  name: string;
  value: number;
  color: string;
  pct: number;
}

export interface DoughnutChartProps {
  data: DoughnutSlice[];
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  /** KPI name used in empty/error state messages */
  kpiName?: string;
  /** Label below the center value (e.g. "Total") */
  centerLabel?: string;
  /** Pre-formatted string shown in the doughnut center */
  centerValue?: string;
  /** Formats slice values for tooltip and legend */
  formatValue?: (v: number) => string;
  /** Diameter of the doughnut circle in px */
  chartDiameter?: number;
  /** Min-height for empty/error containers */
  minHeight?: number;
}

// Display percentages allocated once at 0.01% granularity (Largest Remainder
// Method on hundredths of a percent) so the set always sums to exactly
// 100.00%. Any revenue-bearing entry is guaranteed a minimum 0.01% — taken
// from the largest entry — so it never reads as 0.00%. Exported so report
// exports (CSV/PDF) can emit the exact percentages the widget displays.
export const computeDisplayPcts = (values: number[]): string[] => {
  const total = values.reduce((s, v) => s + (v > 0 ? v : 0), 0);
  if (total <= 0) return values.map(() => '0.00');
  const raw = values.map((v) => (v > 0 ? (v / total) * 10000 : 0));
  const cents = raw.map((p) => Math.floor(p));
  let remainder = 10000 - cents.reduce((s, v) => s + v, 0);
  const order = raw
    .map((p, i) => ({ i, frac: p - Math.floor(p) }))
    .sort((a, b) => b.frac - a.frac);
  for (const { i } of order) {
    if (remainder <= 0) break;
    cents[i] += 1;
    remainder -= 1;
  }
  values.forEach((v, i) => {
    if (v > 0 && cents[i] === 0) {
      const maxI = cents.indexOf(Math.max(...cents));
      if (cents[maxI] > 1) {
        cents[maxI] -= 1;
        cents[i] = 1;
      }
    }
  });
  return cents.map((c) => (c / 100).toFixed(2));
};

const shimmer: React.CSSProperties = {
  background: 'linear-gradient(90deg,#e8eaf0 25%,#f3f4f8 37%,#e8eaf0 63%)',
  backgroundSize: '800px 100%',
  animation: 'shimmer 1.4s ease infinite',
};

const DoughnutChart = ({
  data,
  loading = false,
  error = false,
  onRetry,
  kpiName = 'data',
  centerLabel = 'Total',
  centerValue,
  formatValue = (v: number) =>
    `${getCurrencySymbol()}${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  chartDiameter = 160,
  minHeight = 200,
}: DoughnutChartProps) => {
  // activeIndex — driven by hover (slice/legend) and click; controls chart opacity
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  // pinnedIndex — driven only by clicking a slice; survives mouseleave; drives mobile inline tooltip
  const [pinnedIndex, setPinnedIndex] = useState<number | null>(null);
  // tooltipPos — set by mousemove (desktop) or click; cleared on mouseleave
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  // chartRef — wraps only the donut SVG so we can detect taps/clicks outside the slices
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const dismiss = () => {
      setActiveIndex(null);
      setPinnedIndex(null);
      setTooltipPos(null);
    };
    // Dismiss the pinned tooltip when tapping/clicking anywhere outside the donut
    // (legend, card, page margin, etc.). Taps on a slice are handled by the Pie's
    // own onClick, which toggles the tooltip.
    const handleOutside = (e: Event) => {
      if (chartRef.current && !chartRef.current.contains(e.target as Node)) dismiss();
    };
    window.addEventListener('scroll', dismiss, { passive: true, capture: true });
    document.addEventListener('pointerdown', handleOutside, true);
    return () => {
      window.removeEventListener('scroll', dismiss, { capture: true } as any);
      document.removeEventListener('pointerdown', handleOutside, true);
    };
  }, []);

  if (error) {
    return <SectionError kpiName={kpiName} onRetry={onRetry ?? (() => {})} minHeight={minHeight} />;
  }

  if (!loading && data.length === 0) {
    return <SectionEmpty kpiName={kpiName} minHeight={minHeight} />;
  }

  if (loading) {
    const cr = chartDiameter / 2;
    const outerR = cr - 12;
    const innerR = outerR * 0.72;
    const strokeW = outerR - innerR;
    const midR = innerR + strokeW / 2;
    const circ = 2 * Math.PI * midR;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, minHeight }}>
        <svg width={chartDiameter} height={chartDiameter} style={{ flexShrink: 0 }}>
          <circle cx={cr} cy={cr} r={midR} fill="none" stroke="#e8eaf0" strokeWidth={strokeW} />
          <circle
            cx={cr} cy={cr} r={midR} fill="none" stroke="#f3f4f8" strokeWidth={strokeW}
            strokeDasharray={`${circ * 0.3} ${circ}`}
            style={{ animation: 'spin 1.2s linear infinite', transformOrigin: `${cr}px ${cr}px` }}
          />
        </svg>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[80, 60, 70, 50].map((w, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, ...shimmer }} />
              <div style={{ flex: 1, height: 11, borderRadius: 4, ...shimmer }} />
              <div style={{ width: w, height: 11, borderRadius: 4, ...shimmer }} />
              <div style={{ width: 30, height: 11, borderRadius: 4, ...shimmer }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const cx = chartDiameter / 2;
  const cy = chartDiameter / 2;
  const outerRadius = cx - 12;
  const innerRadius = outerRadius * 0.72;

  const activeSlice = activeIndex !== null ? data[activeIndex] : null;
  const pinnedSlice = pinnedIndex !== null ? data[pinnedIndex] : null;
  // Slice shown in the tooltip — prefer hover (active), fall back to a pinned tap.
  const tooltipSlice = activeSlice ?? pinnedSlice;

  const handleContainerMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (activeIndex !== null) setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const handleContainerMouseLeave = () => {
    setActiveIndex(null);
    // When a slice is pinned (e.g. mobile tap), keep the tooltip + position so the
    // vertical tooltip stays visible after the synthetic mouseleave fires.
    if (pinnedIndex === null) setTooltipPos(null);
  };

  // Clicking a slice toggles the pinned state (mobile tap persists; desktop click pins)
  const handlePieClick = (_: any, index: number, event: React.MouseEvent) => {
    const next = pinnedIndex === index ? null : index;
    setPinnedIndex(next);
    setActiveIndex(next);
    if (next === null) {
      setTooltipPos(null);
    } else if (event?.clientX) {
      setTooltipPos({ x: event.clientX, y: event.clientY });
    }
  };

  // Display index for chart opacity — prefer activeIndex (hover), fall back to pinnedIndex
  const highlightIndex = activeIndex ?? pinnedIndex;

  const displayPcts = computeDisplayPcts(data.map((d) => d.value));
  const tooltipIndex = activeIndex ?? pinnedIndex;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontFamily: T.font }}>
      {/* Chart + center label + desktop hover tooltip */}
      <div
        ref={chartRef}
        style={{ position: 'relative', cursor: 'pointer', flexShrink: 0 }}
        onMouseMove={handleContainerMouseMove}
        onMouseLeave={handleContainerMouseLeave}
      >
        <PieChart width={chartDiameter} height={chartDiameter}>
          <Pie
            data={data}
            cx={cx}
            cy={cy}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            dataKey="value"
            startAngle={90}
            endAngle={-270}
            paddingAngle={data.length > 1 ? 1 : 0}
            stroke="none"
            isAnimationActive={false}
            onMouseEnter={(_: any, index: number) => setActiveIndex(index)}
            onClick={handlePieClick}
          >
            {data.map((entry, idx) => (
              <Cell
                key={`cell-${idx}`}
                fill={entry.color}
                opacity={highlightIndex === null || highlightIndex === idx ? 0.92 : 0.38}
              />
            ))}
          </Pie>
        </PieChart>

        {/* Center value */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{ textAlign: 'center', maxWidth: innerRadius * 1.8, padding: '0 4px' }}>
            <div style={{
              fontSize: chartDiameter < 150 ? 11 : 13,
              fontWeight: 700,
              color: T.t1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {centerValue ?? ''}
            </div>
            <div style={{ fontSize: 9, color: T.t3, marginTop: 2 }}>{centerLabel}</div>
          </div>
        </div>

        {/* Cursor-following tooltip — desktop hover and mobile tap (data stacked vertically) */}
        {tooltipSlice && tooltipPos && (
          <div style={{
            position: 'fixed',
            left: tooltipPos.x + 14,
            top: tooltipPos.y + 14,
            zIndex: 1000,
            background: T.surface,
            border: `1px solid ${T.cardBorder}`,
            borderRadius: 8,
            padding: '8px 12px',
            boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
            pointerEvents: 'none',
            fontFamily: T.font,
            minWidth: 130,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: tooltipSlice.color, flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{tooltipSlice.name}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.t1, marginLeft: 14 }}>
              {formatValue(tooltipSlice.value)}
            </div>
            <div style={{ fontSize: 11, color: T.t3, marginLeft: 14, marginTop: 1 }}>
              {tooltipIndex !== null ? displayPcts[tooltipIndex] : tooltipSlice.pct}%
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ width: '100%', marginTop: 10 }}>
        {data.map((item, idx) => (
          <div
            key={`legend-${idx}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 0',
              borderBottom: idx < data.length - 1 ? `1px solid ${T.divider}` : 'none',
            }}
          >
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, flex: 1, color: T.t1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {item.name}
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>
              {formatValue(item.value)}
            </span>
            <span style={{ fontSize: 11, color: T.t3, minWidth: 32, textAlign: 'right', whiteSpace: 'nowrap' }}>
              {displayPcts[idx]}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DoughnutChart;
